import type { ReactNode } from "react";
import { Alert, Box, CircularProgress, Button, Stack, Typography } from "@mui/material";
import { useSessionLoader } from "@/store/session";
import { useAuth } from "@/store/auth";
import { errorMessage } from "@/api/client";

/**
 * Holds the shell back until <code>GET /me</code> has answered.
 *
 * <p><b>Why gate rather than render optimistically.</b> The sidebar is built from permissions and
 * per-tenant feature flags. Rendering before they arrive shows a menu that then rearranges itself
 * under the user's cursor — and worse, briefly offers items their tenant does not have. A short,
 * honest wait beats a menu that lies for 300ms.</p>
 *
 * <p>A failure here is not recoverable by carrying on: without the feature list the shell cannot
 * decide what to show, so it says so and offers a retry rather than rendering something misleading.</p>
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { isPending, isError, error, refetch } = useSessionLoader();
  const clear = useAuth((s) => s.clear);

  if (isPending) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading your workspace…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", p: 3 }}>
        <Stack spacing={2} sx={{ maxWidth: 460 }}>
          <Alert severity="error">
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Could not load your workspace
            </Typography>
            <Typography variant="body2">{errorMessage(error)}</Typography>
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => refetch()}>
              Try again
            </Button>
            <Button onClick={clear}>Sign out</Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return <>{children}</>;
}
